import { formatLocaleDecimalMax2 } from "./locale_decimal_max_2";

export type SupplyWalkVariant = "standard" | "for_hubs";

export type CommonSupplyWalkCellContextV1 = Readonly<{
  version: "CommonSupplyWalkCellContext.v1";
  surfaceId: "supply-walk";
  variant: SupplyWalkVariant;
  receiptId: string;
  measure: "ending_inventory";
  cellKind: "period_leaf";
  period: Readonly<{
    field: "week";
    grain: "week";
    startEpochMs: number;
    endExclusiveEpochMs: number;
  }>;
  dimensions: readonly Readonly<{ field: "sku" | "location"; value: string }>[];
  selector: Readonly<{
    segmentation: string;
    measure: "ending inventory on hand";
    measureRank: 6 | 8 | 9;
    columnKey: string;
  }>;
  filters: readonly [];
  postFilters: readonly [];
  display: Readonly<{
    rawValue: string;
    unit: "units";
    rule: "inventory_matrix_v1";
  }>;
  state: Readonly<{
    basis: "base";
    edits: "none";
    comparison: false;
  }>;
}>;

export type SupplyWalkCellContextInput = Readonly<{
  variant: unknown;
  receiptId: unknown;
  measure: unknown;
  measureRank: unknown;
  periodField: unknown;
  periodStartEpochMs: unknown;
  segments: readonly unknown[] | undefined;
  dimensions: Readonly<Record<string, unknown>>;
  rawValue: unknown;
  editMode: unknown;
  dirty: unknown;
  comparison: unknown;
}>;

const RECEIPT_ID = /^rvr1_[a-f0-9]{64}$/;
const EXACT_SEGMENTS = Object.freeze(["sku", "location"] as const);

const utf8ByteLength = (value: string): number => {
  let bytes = 0;
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit < 0x80) bytes += 1;
    else if (codeUnit < 0x800) bytes += 2;
    else if (
      codeUnit >= 0xd800 &&
      codeUnit <= 0xdbff &&
      index + 1 < value.length &&
      value.charCodeAt(index + 1) >= 0xdc00 &&
      value.charCodeAt(index + 1) <= 0xdfff
    ) {
      bytes += 4;
      index += 1;
    } else bytes += 3;
  }
  return bytes;
};

const boundedText = (value: unknown, maxBytes: number): string | undefined =>
  typeof value === "string" &&
  value.length > 0 &&
  value === value.trim() &&
  utf8ByteLength(value) <= maxBytes &&
  !/\p{Cc}/u.test(value)
    ? value
    : undefined;

const isVariant = (value: unknown): value is SupplyWalkVariant =>
  value === "standard" || value === "for_hubs";

const isSupportedMeasureRank = (
  variant: SupplyWalkVariant,
  value: unknown,
): value is 6 | 8 | 9 =>
  Number.isSafeInteger(value) &&
  (variant === "for_hubs" ? value === 8 : value === 6 || value === 9);

const hasExactSegments = (
  value: readonly unknown[] | undefined,
): value is readonly ["sku", "location"] =>
  Array.isArray(value) &&
  value.length === EXACT_SEGMENTS.length &&
  value.every((segment, index) => segment === EXACT_SEGMENTS[index]);

const weekEndExclusive = (value: unknown): number | undefined => {
  if (!Number.isSafeInteger(value) || (value as number) <= 0) return undefined;
  const start = new Date(value as number);
  if (
    Number.isNaN(start.getTime()) ||
    start.getUTCDay() !== 1 ||
    start.getUTCHours() !== 0 ||
    start.getUTCMinutes() !== 0 ||
    start.getUTCSeconds() !== 0 ||
    start.getUTCMilliseconds() !== 0
  ) {
    return undefined;
  }
  return Date.UTC(
    start.getUTCFullYear(),
    start.getUTCMonth(),
    start.getUTCDate() + 7,
  );
};

export const normalizeSupplyWalkSavedValue = (
  value: unknown,
): string | undefined => {
  if (
    (typeof value !== "number" && typeof value !== "string") ||
    (typeof value === "string" &&
      (value.length === 0 || value !== value.trim() || value.length > 128))
  ) {
    return undefined;
  }
  const normalized =
    typeof value === "string" && value.includes(",")
      ? /^-?(?:0|[1-9]\d{0,2}(?:,\d{3})+)(?:\.\d+)?$/.test(value)
        ? value.replace(/,/g, "")
        : undefined
      : value;
  if (normalized === undefined) return undefined;
  const numeric = Number(normalized);
  if (!Number.isFinite(numeric)) return undefined;
  return Object.is(numeric, -0) ? "0" : String(numeric);
};

export function createSupplyWalkCellContext(
  input: SupplyWalkCellContextInput,
): CommonSupplyWalkCellContextV1 | undefined {
  if (
    !isVariant(input.variant) ||
    typeof input.receiptId !== "string" ||
    !RECEIPT_ID.test(input.receiptId) ||
    typeof input.measure !== "string" ||
    input.measure.trim().toLowerCase() !== "ending inventory on hand" ||
    !isSupportedMeasureRank(input.variant, input.measureRank) ||
    input.periodField !== "week" ||
    !hasExactSegments(input.segments) ||
    input.editMode !== false ||
    input.dirty !== false ||
    input.comparison !== false
  ) {
    return undefined;
  }
  const endExclusiveEpochMs = weekEndExclusive(input.periodStartEpochMs);
  const sku = boundedText(input.dimensions?.sku, 512);
  const location = boundedText(input.dimensions?.location, 512);
  const rawValue = normalizeSupplyWalkSavedValue(input.rawValue);
  if (!endExclusiveEpochMs || !sku || !location || rawValue === undefined) {
    return undefined;
  }
  const dimensions = Object.freeze([
    Object.freeze({ field: "sku" as const, value: sku }),
    Object.freeze({ field: "location" as const, value: location }),
  ]);
  const startEpochMs = input.periodStartEpochMs as number;
  return Object.freeze({
    version: "CommonSupplyWalkCellContext.v1",
    surfaceId: "supply-walk",
    variant: input.variant,
    receiptId: input.receiptId,
    measure: "ending_inventory",
    cellKind: "period_leaf",
    period: Object.freeze({
      field: "week",
      grain: "week",
      startEpochMs,
      endExclusiveEpochMs,
    }),
    dimensions,
    selector: Object.freeze({
      segmentation: `${sku} | ${location}`,
      measure: "ending inventory on hand",
      measureRank: input.measureRank,
      columnKey: String(startEpochMs),
    }),
    filters: Object.freeze([]) as readonly [],
    postFilters: Object.freeze([]) as readonly [],
    display: Object.freeze({
      rawValue,
      unit: "units",
      rule: "inventory_matrix_v1",
    }),
    state: Object.freeze({ basis: "base", edits: "none", comparison: false }),
  });
}

export function isSupportedSupplyWalkCellContext(
  context: CommonSupplyWalkCellContextV1,
): boolean {
  return (
    context.version === "CommonSupplyWalkCellContext.v1" &&
    context.surfaceId === "supply-walk" &&
    isVariant(context.variant) &&
    RECEIPT_ID.test(context.receiptId) &&
    context.measure === "ending_inventory" &&
    context.cellKind === "period_leaf" &&
    context.period.field === "week" &&
    context.period.grain === "week" &&
    weekEndExclusive(context.period.startEpochMs) ===
      context.period.endExclusiveEpochMs &&
    context.dimensions.length === 2 &&
    context.dimensions[0]?.field === "sku" &&
    Boolean(boundedText(context.dimensions[0]?.value, 512)) &&
    context.dimensions[1]?.field === "location" &&
    Boolean(boundedText(context.dimensions[1]?.value, 512)) &&
    context.selector.segmentation ===
      context.dimensions.map(({ value }) => value).join(" | ") &&
    context.selector.measure === "ending inventory on hand" &&
    isSupportedMeasureRank(context.variant, context.selector.measureRank) &&
    context.selector.columnKey === String(context.period.startEpochMs) &&
    context.filters.length === 0 &&
    context.postFilters.length === 0 &&
    normalizeSupplyWalkSavedValue(context.display.rawValue) ===
      context.display.rawValue &&
    context.display.unit === "units" &&
    context.display.rule === "inventory_matrix_v1" &&
    context.state.basis === "base" &&
    context.state.edits === "none" &&
    context.state.comparison === false
  );
}

export function supplyWalkCellPrompt(
  context: CommonSupplyWalkCellContextV1,
): string {
  const sku = context.dimensions[0].value;
  const location = context.dimensions[1].value;
  const numericValue = Number(context.display.rawValue);
  const displayedValue =
    formatLocaleDecimalMax2(numericValue, "en-US") ?? context.display.rawValue;
  const week = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(context.period.startEpochMs));
  const contributors =
    context.variant === "for_hubs"
      ? "saved starting inventory, forecasted sales, transfer departures, and purchase-order arrivals"
      : "saved starting inventory, forecasted sales, and purchase-order arrivals";
  return `Explain the saved ending-inventory balance of ${displayedValue} units for SKU ${sku} at ${location} for the week of ${week}. Reconcile it from ${contributors}, including the floor-at-zero and unmet-demand effects when relevant. Show the exact contributors and calculation, distinguish saved-view evidence from transformation or raw-source lineage, recommend the next useful inspection, and clearly state anything the available evidence cannot establish.`;
}
